import { Datagrid, DateField, List, ReferenceField, TextField, TextInput } from 'react-admin';
import { useNotify, useRedirect } from 'react-admin';
import { useState } from 'react';
import axios from 'axios';
import { TextField as MuiTextField, MenuItem, Button, LinearProgress, Typography, Card, CardContent } from '@mui/material';
import inMemoryJWT from './inMemoryJWT';
import { apiBaseUrl } from './config';

const firmwareFilters = [
    <TextInput source="q" label="Search" alwaysOn />,
];

export const FirmwareList = () => (
    <List hasCreate perPage={50} sort={{ field: 'upload_ts', order: 'DESC' }} filters={firmwareFilters}>
        <Datagrid>
            <TextField label="Model" source="model" />
            <TextField label="FW Name" source="fw_name" />
            <TextField label="FW Type" source="fw_type" />
            <TextField label="Version" source="version" />
            <TextField label="MD5 Checksum" source="md5" />
            <DateField label="Date" source="upload_ts" showTime />
        </Datagrid>
    </List>
);

const fw_type_choices = [
    { id: 'fw', name: 'Dashcam Firmware' },
    { id: 'ai_fw', name: 'AI Firmware' },
    { id: 'modem_fw', name: 'Modem Firmware' },
]

const fw_model_choices = [
    { id: 'T5304V2', name: 'Dashcam T5304V2' },
    { id: 'DC082', name: 'Dashcam DC082' },
    { id: 'T8112B', name: 'Dashcam ET12 (T8112B)' },
]

const ai_fw_model_choices = [
    { id: 'T5304V2-AI', name: 'T5304V2 AI firmware' },
    { id: 'DC082-AI', name: 'DC082 AI firmware' },
    { id: 'T8112B-AI', name: 'T8112B AI firmware' },
]

const modem_fw_model_choices = [
    { id: 'EG25G-C1', name: 'Modem EG25G-C1' },
]

function validate_no_space(value) {
    return !!value && value.indexOf(' ') >= 0;
}

function modelChoicesForType(fwType) {
    if (fwType === 'fw') return fw_model_choices;
    if (fwType === 'ai_fw') return ai_fw_model_choices;
    if (fwType === 'modem_fw') return modem_fw_model_choices;
    return [];
}

function formatBytes(n) {
    if (!n) return '0 B';
    var units = ['B', 'KB', 'MB', 'GB'];
    var i = 0;
    var v = n;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
    }
    return v.toFixed(1) + ' ' + units[i];
}

/* Custom upload (not react-admin's default dataProvider.create() flow, which goes through
   fetch() and has no way to report upload progress at all) so a real byte-level progress bar
   can be shown - axios wraps XMLHttpRequest, which exposes upload.onprogress, unlike fetch(). */
export const FirmwareCreate = () => {
    const notify = useNotify();
    const redirect = useRedirect();
    const [fwName, setFwName] = useState('');
    const [version, setVersion] = useState('');
    const [fwType, setFwType] = useState('');
    const [model, setModel] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [uploadedBytes, setUploadedBytes] = useState(0);
    const [totalBytes, setTotalBytes] = useState(0);

    function handleSubmit(e) {
        e.preventDefault();
        if (!fwName || validate_no_space(fwName)) {
            notify('FW Name is required and must not contain spaces', { type: 'error' });
            return;
        }
        if (!version || validate_no_space(version)) {
            notify('Version is required and must not contain spaces', { type: 'error' });
            return;
        }
        if (!fwType) {
            notify('FW Type is required', { type: 'error' });
            return;
        }
        if (!model) {
            notify('Model is required', { type: 'error' });
            return;
        }
        if (!file) {
            notify('Firmware file is required', { type: 'error' });
            return;
        }
        const token = inMemoryJWT.getToken();
        if (!token) {
            notify('No token found', { type: 'error' });
            return;
        }

        var formData = new FormData();
        formData.append('fw_file', file);
        var urlParams = new URLSearchParams({ fw_name: fwName, fw_type: fwType, version: version, model: model });

        setUploading(true);
        setProgress(0);
        setUploadedBytes(0);
        setTotalBytes(0);

        axios.post(apiBaseUrl + '/upload_fw?' + urlParams.toString(), formData, {
            headers: { 'x-access-token': token },
            onUploadProgress: function(progressEvent) {
                setUploadedBytes(progressEvent.loaded);
                if (progressEvent.total) {
                    setTotalBytes(progressEvent.total);
                    setProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                }
            },
        }).then(function(response) {
            var data = response && response.data ? response.data : null;
            if (!data || data.error) {
                notify('Upload failed: ' + (data && data.error ? data.error : 'Invalid response'), { type: 'error' });
                return;
            }
            notify('Firmware uploaded', { type: 'success' });
            redirect('/firmwares');
        }).catch(function(err) {
            var serverMsg = err && err.response && err.response.data;
            var msg = (serverMsg && serverMsg.error) || (typeof serverMsg === 'string' ? serverMsg : null)
                || (err && err.message) || String(err);
            notify('Upload failed: ' + msg, { type: 'error' });
        }).finally(function() {
            setUploading(false);
        });
    }

    return (
        <Card style={{ maxWidth: '600px', margin: '24px' }}>
            <CardContent>
                <Typography variant="h6" style={{ marginBottom: '16px' }}>Create Firmware</Typography>
                <form onSubmit={handleSubmit}>
                    <MuiTextField fullWidth margin="normal" label="FW Name" value={fwName}
                        onChange={function(e) { setFwName(e.target.value); }} disabled={uploading} />
                    <MuiTextField fullWidth margin="normal" label="Version" value={version}
                        onChange={function(e) { setVersion(e.target.value); }} disabled={uploading} />
                    <MuiTextField select fullWidth margin="normal" label="FW Type" value={fwType}
                        onChange={function(e) { setFwType(e.target.value); setModel(''); }} disabled={uploading}>
                        {fw_type_choices.map(function(c) {
                            return <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>;
                        })}
                    </MuiTextField>
                    <MuiTextField select fullWidth margin="normal" label="Model" value={model}
                        onChange={function(e) { setModel(e.target.value); }} disabled={uploading || !fwType}>
                        {modelChoicesForType(fwType).map(function(c) {
                            return <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>;
                        })}
                    </MuiTextField>
                    <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                        <input type="file" onChange={function(e) { setFile(e.target.files[0] || null); }} disabled={uploading} />
                    </div>
                    {uploading && (
                        <div style={{ marginBottom: '16px' }}>
                            <LinearProgress variant="determinate" value={progress} />
                            <Typography variant="body2" style={{ marginTop: '4px' }}>
                                {progress}% ({formatBytes(uploadedBytes)} / {formatBytes(totalBytes)})
                            </Typography>
                        </div>
                    )}
                    <Button type="submit" variant="contained" disabled={uploading}>
                        {uploading ? 'Uploading...' : 'Save'}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
};
